import React from "react";
import { Link } from "gatsby";
import SafeSiteImage from "./SafeSiteImage";
import styled, { css } from "styled-components";
import { ProductSpecification } from "./ProductSpecification";

const ArticleCardStyles = styled(({ className, to, children }) => <Link to={to} className={className}>{children}</Link>)`
  ${({ ...props }) => css`
    display: flex;
    flex-flow: column;
    background: ${(props.$border ? `#ECECE9` : "#fff")};
    background: ${props.$category && props.theme.categoryColor};
    color: ${props.$category && "#fff"};
    border-radius: ${(props.$secondary ? "4px 4px 0 0" : "4px")};
    overflow: hidden;
    transition: box-shadow ease-in-out 0.3s;
    border-bottom: ${props.$secondary ? `solid 8px ${props.theme.colors.primary}` : "none"};
    position: relative;

    .inline-image {
      aspect-ratio: 4 / 3;
    }
  
    .inline-image img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    div:has(.image),
    .inline-image {
      z-index: 0;
    }

    img {
      transition: transform .5s ease-in;
    }

    &:hover .image:not(.rollover-image) {
      img {
        transform: scale(1.1)
      }
    }

    .image {
      width: 100% !important;

      * {
        width: 100% !important;
      }

      img {
        object-fit: cover;
        aspect-ratio: 4/3;
        height: auto;
      }
    }

    .rollover-image,
    .specification {
      display: none;
    }

    &:hover {
      box-shadow: 0px -4px 17px 0px rgb(68 68 68 / 55%);
      color: ${props.$category && "#fff"};
    }

    h3 {
      font-size: 1rem;
      line-height: 20px;
    }

    @media (min-width: ${props.theme.breakpoints.lg}) {
      flex-flow: ${props.$inline ? 'row' : 'column'};

      .inline-image {
        aspect-ratio: 4 / 3;
        max-width: 50%;
      }
      /*
        ToDo
         Animate this
      */
      &:hover {
        .rollover-image,
        .specification {
          display: block;
        }

        .default-image {
          display: none;
        }
      }
    }
  `}
`;

const CallToActionStyles = styled.span`
  ${({ ...props }) => css`
    color: ${props.theme.colors.primary};
    display: none;

    @media (min-width: ${props.theme.breakpoints.lg}) {
      display: block;
    }
  `}
`;

const ArticleCardBodyWrapperStyles = styled.div`
  ${({ ...props }) => css`
    display: flex;
    flex-flow: column;
    justify-content: space-between;
    flex-grow: 1;
    container-type: inline-size;
    z-index: 1;
    background: ${(props.$border ? `#ECECE9` : "#fff")};
    background: ${props.$category && props.theme.categoryColor};

    @container (max-width: 240px) {
      .card_price__wrapper {
        flex-flow: column;
        width: fit-content
      }
    }
  `}
`;

const ArticleCardBodyStyles = styled.div`
  ${({ ...props }) => css`
    padding: 10px 24px 16px 24px;
    display: flex;
    flex-direction: column;
    flex-grow: 1;

    h3 {
      flex-grow: 1;
    }

    :has(+div) {
      padding-bottom: 10px;
    }

    @media (min-width: ${props.theme.breakpoints.lg}) {
      padding: ${(props.$inline ? "24px" : "10px 24px 16px 24px")};
    }
  `}
`;

const SpecificationStyles = styled.div`
  ${({ ...props }) => css`
    @media (min-width: ${props.theme.breakpoints.lg}) {
      background: #fff;
      background: ${props.$category && props.theme.categoryColor};
      color: ${props.$category && "#fff"};
      padding: 16px 24px;
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%!important;
      font-size: .75rem;
      overflow: hidden;
      border-bottom-left-radius: 4px;
      border-bottom-right-radius: 4px;
      z-index: 2;
    }
  `}
`;

const PriceStyles = styled.div`
  ${({ ...props }) => css`
    margin: 0 24px;
    padding: 0px 0 16px;
    display: flex;
    gap: .5rem;
    justify-content: space-between;

    .card_price {
      display: grid;
      grid-template-columns: auto auto;
      align-items: center;
      gap: .2rem
    }
    .card_price--type {
      grid-column: span 2;
      font-size: .8rem;
      line-height: 1.2;
    }
    .card_price--price {
      font-weight: bold;
      font-size: 1.33rem;
      padding-top: .1rem;
    }
    .card_price--info {
      font-size: .6rem;
      line-height: 1;
      max-width: 2rem;
    }
    .card_price__separator {
      width: 1px;
      display: block;
      background-color: white;
    }
  `}
`

export const ArticleCard = ({
  url,
  title,
  featuredImage,
  callToAction,
  catColour,
  category,
  secondary,
  inline,
  product,
  rolloverImage,
  specification,
  sliderCard,
  border,
  hirePrice,
  buyPrice,
}) => {
  return (
    <ArticleCardStyles
      to={url}
      className={catColour ? "cat-bg" : ``}
      $secondary={secondary}
      $category={category}
      $border={border}
      $inline={inline}
    >
      {inline ? (
        <div className="inline-image">
          <SafeSiteImage image={featuredImage} alt={title} width="591" height="443" fit="cover" />
        </div>
      ) : (
        <div>
          {featuredImage && (
            <div className={`image ${rolloverImage && "default-image" || ''}`}>
              <SafeSiteImage image={featuredImage} alt={title} width="543" height="407" fit="cover" />
            </div>
          )}
          {rolloverImage && (
            <div className="image rollover-image">
              <SafeSiteImage image={rolloverImage} alt={title} width="543" height="407" fit="cover" />
            </div>
          )}
        </div>
      )}
      <ArticleCardBodyWrapperStyles
        className={catColour ? "cat-bg" : ``}
        $category={category}
        $border={border}
      >
        <ArticleCardBodyStyles
          $secondary={secondary}
          $inline={inline}
          $product={product}
        >
          <h3>{title}</h3>
          {callToAction && (
            <CallToActionStyles>{callToAction}</CallToActionStyles>
          )}
        </ArticleCardBodyStyles>
        {(buyPrice?.price || buyPrice?.priceOnApplication || hirePrice?.price || hirePrice?.priceOnApplication) && (
          <PriceStyles className="card_price__wrapper">
            {hirePrice?.priceOnApplication && (
              <div className="card_price">
                <span className="card_price--type">
                  Hire from
                </span>
                <span className="card_price--price">
                  POA
                </span>
              </div>
            )}
            {!hirePrice?.priceOnApplication && hirePrice?.price && (
              <div className="card_price">
                <span className="card_price--type">
                  Hire from
                </span>
                <span className="card_price--price">
                  £{hirePrice.price.toFixed(2)}
                </span>
                <span className="card_price--info">
                  ex. VAT
                </span>
              </div>
            )}
            {(buyPrice?.price && hirePrice?.price) && (
              <span className="card_price__separator" />
            )}
            {buyPrice?.priceOnApplication && (
              <div className="card_price">
                <span className="card_price--type">
                  Buy from
                </span>
                <span className="card_price--price">
                  POA
                </span>
              </div>
            )}
            {!buyPrice?.priceOnApplication && buyPrice?.price && (
              <div className="card_price">
                <span className="card_price--type">
                  Buy from
                </span>
                <span className="card_price--price">
                  £{buyPrice.price.toFixed(2)}
                </span>
                <span className="card_price--info">
                  ex. VAT
                </span>
              </div>
            )}
          </PriceStyles>
        )}
      </ArticleCardBodyWrapperStyles>
      {specification && (
        <>
          {specification.height ||
          specification.depth ||
          specification.width ||
          specification.weight ||
          specification.filledWeight ? (
            <SpecificationStyles
              $sliderCard={sliderCard}
              className={`${catColour ? "cat-bg" : ``} specification`}
              $category={category}
            >
              <ProductSpecification
                specification={specification}
                small
              />
            </SpecificationStyles>
          ) : (
            ""
          )}
        </>
      )}
    </ArticleCardStyles>
  );
};
